import type { ExtendFile } from './types';

// ----------------------------------------------------------------------

// Define more types here
const FORMAT_PDF = ['pdf'];
const FORMAT_TEXT = ['txt'];
const FORMAT_PHOTOSHOP = ['psd'];
const FORMAT_DOC = ['doc'];
const FORMAT_DOCX = ['docx'];
const FORMAT_XLS = ['xls'];
const FORMAT_XLSX = ['xlsx'];
const FORMAT_ZIP = ['zip', 'rar', 'iso'];
const FORMAT_ADOC = ['adoc'];
const FORMAT_ASICE = ['asice'];
const FORMAT_EDOC = ['edoc'];
const FORMAT_BDOC = ['bdoc'];
const FORMAT_ILLUSTRATOR = ['ai', 'esp'];
const FORMAT_PPT = ['ppt'];
const FORMAT_PPTX = ['pptx'];
const FORMAT_AUDIO = ['wav', 'aif', 'mp3', 'aac'];
const FORMAT_IMG = ['jpg', 'jpeg', 'gif', 'bmp', 'png', 'svg', 'webp'];
const FORMAT_VIDEO = ['m4v', 'avi', 'mpg', 'mp4', 'webm'];

const iconUrl = (icon: string) => `/icons/${icon}.svg`;

// ----------------------------------------------------------------------

export function fileFormat(fileUrl: string) {
  let format;

  if (!fileUrl) {
    return '';
  }

  const fileByUrl = fileTypeByUrl(fileUrl);

  switch (fileUrl.toLowerCase().includes(fileByUrl)) {
    case FORMAT_TEXT.includes(fileByUrl):
      format = 'txt';
      break;
    case FORMAT_ZIP.includes(fileByUrl):
      format = 'zip';
      break;
    case FORMAT_ADOC.includes(fileByUrl):
      format = 'adoc';
      break;
    case FORMAT_ASICE.includes(fileByUrl):
      format = 'asice';
      break;
    case FORMAT_BDOC.includes(fileByUrl):
      format = 'bdoc';
      break;
    case FORMAT_EDOC.includes(fileByUrl):
      format = 'edoc';
      break;
    case FORMAT_AUDIO.includes(fileByUrl):
      format = 'audio';
      break;
    case FORMAT_IMG.includes(fileByUrl):
      format = 'image';
      break;
    case FORMAT_VIDEO.includes(fileByUrl):
      format = 'video';
      break;
    case FORMAT_DOC.includes(fileByUrl):
      format = 'doc';
      break;
    case FORMAT_DOCX.includes(fileByUrl):
      format = 'docx';
      break;
    case FORMAT_XLS.includes(fileByUrl):
      format = 'xls';
      break;
    case FORMAT_XLSX.includes(fileByUrl):
      format = 'xlsx';
      break;
    case FORMAT_PPT.includes(fileByUrl):
      format = 'ppt';
      break;
    case FORMAT_PPTX.includes(fileByUrl):
      format = 'pptx';
      break;
    case FORMAT_PDF.includes(fileByUrl):
      format = 'pdf';
      break;
    case FORMAT_PHOTOSHOP.includes(fileByUrl):
      format = 'photoshop';
      break;
    case FORMAT_ILLUSTRATOR.includes(fileByUrl):
      format = 'illustrator';
      break;
    default:
      format = fileTypeByUrl(fileUrl);
  }

  return format;
}

// ----------------------------------------------------------------------

export function fileThumb(fileUrl: string) {
  let thumb;

  switch (fileFormat(fileUrl)) {
    case 'folder':
      thumb = iconUrl('icon_folder');
      break;
    case 'txt':
      thumb = iconUrl('icon_txt');
      break;
    case 'zip':
      thumb = iconUrl('icon_zip');
      break;
    case 'adoc':
      thumb = iconUrl('icon_adoc');
      break;
    case 'asice':
      thumb = iconUrl('icon_asice');
      break;
    case 'bdoc':
      thumb = iconUrl('icon_bdoc');
      break;
    case 'edoc':
      thumb = iconUrl('icon_edoc');
      break;
    case 'audio':
      thumb = iconUrl('icon_audio');
      break;
    case 'video':
      thumb = iconUrl('icon_video');
      break;
    case 'doc':
      thumb = iconUrl('icon_doc');
      break;
    case 'docx':
      thumb = iconUrl('icon_docx');
      break;
    case 'xls':
      thumb = iconUrl('icon_xls');
      break;
    case 'xlsx':
      thumb = iconUrl('icon_xlsx');
      break;
    case 'ppt':
      thumb = iconUrl('icon_ppt');
      break;
    case 'pptx':
      thumb = iconUrl('icon_pptx');
      break;
    case 'pdf':
      thumb = iconUrl('icon_pdf');
      break;
    case 'photoshop':
      thumb = iconUrl('icon_pts');
      break;
    case 'illustrator':
      thumb = iconUrl('icon_ai');
      break;
    case 'image':
      thumb = iconUrl('icon_img');
      break;
    default:
      thumb = iconUrl('icon_file');
  }
  return thumb;
}

// ----------------------------------------------------------------------

export function fileTypeByUrl(fileUrl: string) {
  return (fileUrl && fileUrl.split('.').pop()?.toLowerCase()) || '';
}

// ----------------------------------------------------------------------

export function fileNameByUrl(fileUrl: string) {
  return fileUrl.split('/').pop();
}

// ----------------------------------------------------------------------

export function fileData(file: File | string) {
  // From url
  if (typeof file === 'string') {
    return {
      preview: file,
      name: fileNameByUrl(file),
      type: fileTypeByUrl(file),
      size: undefined,
      path: file,
      lastModified: undefined,
      lastModifiedDate: undefined,
    };
  }

  // From file
  return {
    name: file.name,
    size: file.size,
    path: (file as ExtendFile).path,
    type: file.type,
    preview: (file as ExtendFile).preview,
    lastModified: file.lastModified,
    lastModifiedDate: (file as ExtendFile).lastModifiedDate,
  };
}

// ----------------------------------------------------------------------

export const ALLOWED_FILES_FOR_VALIDATION = ['pdf', 'adoc', 'asice', 'edoc', 'bdoc'];

// ----------------------------------------------------------------------

export function fileDataFromDocument(doc: { uri: string; fileName?: string; fileType?: string }) {
  const name = doc.fileName || fileNameByUrl(doc.uri) || 'unknown';
  const type = doc.fileType || fileTypeByUrl(doc.uri);

  return {
    name,
    type,
    preview: doc.uri,
    path: doc.uri,
    size: undefined,
    lastModified: undefined,
    lastModifiedDate: undefined,
  };
}
